import { useState, useEffect } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useVehicleMakes } from '@/hooks/useVehicleData';

interface VehicleMakeComboboxProps {
  year: string;
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
}

export const VehicleMakeCombobox = ({
  year,
  value,
  onValueChange,
  disabled,
}: VehicleMakeComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: makes, isLoading } = useVehicleMakes(year, debouncedSearch);

  // Separate popular and regular makes
  const popularMakes = makes?.filter(m => m.popular) || [];
  const otherMakes = makes?.filter(m => !m.popular) || [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          disabled={disabled}
        >
          {value || "Search for a make..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder="Type to search makes..." 
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {isLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Loading...</span>
              </div>
            ) : (
              <>
                <CommandEmpty>No makes found.</CommandEmpty>
                
                {popularMakes.length > 0 && !search && (
                  <CommandGroup heading="Popular Makes">
                    {popularMakes.map((make) => (
                      <CommandItem
                        key={`popular-${make.id}`}
                        value={make.name}
                        onSelect={() => {
                          onValueChange(make.name);
                          setOpen(false);
                          setSearch('');
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            value === make.name ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {make.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                
                {otherMakes.length > 0 && (
                  <CommandGroup heading={search ? "Results" : "All Makes"}>
                    {otherMakes.map((make) => (
                      <CommandItem
                        key={`other-${make.id}`}
                        value={make.name}
                        onSelect={() => {
                          onValueChange(make.name);
                          setOpen(false);
                          setSearch('');
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            value === make.name ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {make.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {search && popularMakes.length > 0 && (
                  <CommandGroup heading="Popular Makes">
                    {popularMakes.map((make) => (
                      <CommandItem
                        key={`search-popular-${make.id}`}
                        value={make.name}
                        onSelect={() => {
                          onValueChange(make.name);
                          setOpen(false);
                          setSearch('');
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            value === make.name ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {make.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
